import {
  makeStyles,
  Padding,
  TableCell,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import { ITableColumn } from "components/interfaces";
import { TAlign } from "components/type";
import { ReactElement } from "react";
import { formatNumber, formatNumberToMonetary } from "utils";

const useStyles = makeStyles({
  truncateText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

interface IRowProps {
  /** Dados da linha. Pode-se passar um ReactElement como valor para criar uma célula customizada. */
  row: { [property: string]: any };
  /** Lista com as configurações de cada coluna. */
  columns: ITableColumn[];
  /** Célula da coluna 'Ação'. Quando definida adiciona uma coluna no final da linha. */
  actionCell?: ReactElement;
  /** Pode receber outras propriedades que usadas no componenete TableRow do material-ui. */
  [property: string]: any;
}

/** Componente linha da tabela genérico. Usado para criar tabelas simples ou com poucos elementos customizados. */
const Row = ({ row, columns, actionCell, ...props }: IRowProps) => {
  const classes = useStyles();

  return (
    <TableRow {...props}>
      {columns.map((column: ITableColumn, index: number) => {
        const label = getCellLabel(row[column.dataKey], column);
        const isDash = label === "-";
        const alignment = isDash ? "center" : (column.align as TAlign);

        if (column.truncateText && !isDash) {
          return (
            <TableCell
              key={`table-cell-${index}`}
              align={alignment}
              padding={column.padding as Padding}
            >
              <Tooltip title={label}>
                <div
                  className={classes.truncateText}
                  style={{ width: column.width || 100 }}
                >
                  {label}
                </div>
              </Tooltip>
            </TableCell>
          );
        }

        return index === 0 ? (
          <TableCell
            key={`table-cell-${index}`}
            component="th"
            scope="row"
            align={alignment}
            colSpan={column.colSpan}
            padding={column.padding as Padding}
          >
            {label}
          </TableCell>
        ) : (
          <TableCell
            key={`table-cell-${index}`}
            align={alignment}
            padding={column.padding as Padding}
          >
            {label}
          </TableCell>
        );
      })}
      {actionCell}
    </TableRow>
  );
};

function getCellLabel(value = "", column: ITableColumn) {
  if (column.monetary) return formatNumberToMonetary(Number(value));
  if (column.numeric) return formatNumber(Number(value), 2, 5);
  return value;
}

export default Row;
